<template>
  <div
    class="form-select"
    :class="{
      showMenu: showOptions,
      'has-error': error,
    }"
    v-closable="closeOptions"
  >
    <input
      class="form-select__input"
      type="text"
      :class="{ 'has-gray-background': hasGrayBackground }"
      v-model="label"
      @keydown.enter="enterKey"
      @keydown.down.prevent="downKey"
      @keydown.up.prevent="upKey"
      @keydown.tab="closeOptions"
      @keyup.esc="closeOptions"
      @keydown.space.prevent="enterKey"
      @focus="openOptions"
      @input="queryEdited"
      @click="queryEdited"
      :placeholder="placeholder"
      :aria-label="placeholder"
      :name="name"
      readonly
    />

    <span v-if="error" class="form-select__error-msg">{{ error }}</span>

    <simplebar class="form-select__menu" v-if="showOptions">
      <ul class="form-select__list">
        <li v-for="(option, index) in options" :key="index" class="form-select__item" @click="selectOption(index)">
          <span
            class="form-select__option"
            v-bind:class="{ active: isActiveItem(index), selected: isSelectedItem(option) }"
            >{{ getOptionText(option) }}</span
          >
        </li>
      </ul>
    </simplebar>
  </div>
</template>

<script>
import simplebar from 'simplebar-vue';

export default {
  name: 'FormSelect',
  data() {
    return {
      query: '',
      label: '',
      isOpen: false,
      currentIdx: 0,
    };
  },
  components: {
    simplebar,
  },
  props: {
    name: {
      type: String,
      required: false,
    },
    // This component can be used without model and use the emitted 'query-edited' event only
    value: {
      type: [Object, String, Number],
      required: false,
    },
    // The options list to display (can be client-side static or provided and updated by a server)
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    // If using objects, this field is used to determine what is the ID property of the object (for comparison purposes)
    idProp: {
      type: String,
      required: false,
    },
    // If using objects, this field is used to determine what is the label property of the object (used for displaying)
    labelProp: {
      type: String,
      required: false,
    },
    // If using objects, this field is used to determine what is the value property of the object (when selecting an option)
    valueProp: {
      type: String,
      required: false,
    },
    hasGrayBackground: {
      type: Boolean,
      required: false,
    },

    // If true, clears the query on item select
    clearOnSelect: {
      type: Boolean,
      required: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: function () {
        return this.$t('form-select.place-holder');
      },
    },
    error: {
      type: String,
      default: null,
    },
  },
  computed: {
    showOptions() {
      return this.isOpen;
    },
  },
  watch: {
    // Keep the displayed label + active index in sync with the bound value — initially
    // (immediate) and whenever the value or the options list changes. The input renders
    // `label`, so this is what makes a programmatically-set value actually show as selected.
    value: {
      immediate: true,
      handler() {
        this.syncFromValue();
      },
    },
    options() {
      this.syncFromValue();
    },
  },
  methods: {
    syncFromValue() {
      const idx = this.options.findIndex((option) => this.getOptionId(option) === this.getOptionId(this.value));
      // -1 (no match) leaves nothing highlighted, which is the intended "no selection" state;
      // selectOption() guards against acting on it. Keep label AND query in sync either way.
      this.currentIdx = idx;
      if (idx >= 0) {
        this.label = this.getOptionText(this.options[idx]) || '';
        this.query = this.getOptionValue(this.options[idx]);
      } else {
        this.label = '';
        this.query = '';
      }
    },
    // Keys act only when the list is open; while closed they open it, so a focused-but-closed
    // select can't be navigated + committed without the options showing.
    upKey() {
      if (!this.isOpen) {
        this.openOptions();
        return;
      }
      if (this.currentIdx > 0) this.currentIdx--;
    },
    downKey() {
      if (!this.isOpen) {
        this.openOptions();
        return;
      }
      if (this.currentIdx < this.options.length - 1) this.currentIdx++;
    },
    enterKey() {
      if (!this.isOpen) {
        this.openOptions();
        return;
      }
      this.selectOption(this.currentIdx);
    },
    isActiveItem(index) {
      return index === this.currentIdx;
    },
    isSelectedItem(option) {
      if (!this.value) return false;
      return this.getOptionId(option) === this.getOptionId(this.value);
    },
    queryEdited() {
      this.isOpen = true;
    },
    openOptions() {
      this.isOpen = true;
    },
    closeOptions() {
      this.isOpen = false;
    },
    selectOption(index) {
      // Ignore out-of-range indices (e.g. currentIdx === -1 when the bound value matches no
      // option) so keyboard Enter can't select a non-existent option and emit `undefined`.
      if (index < 0 || index > this.options.length - 1) return;
      let option = this.options[index];
      this.label = this.clearOnSelect ? '' : this.getOptionText(option) || '';
      this.query = this.getOptionValue(option);
      this.$emit('input', this.getOptionValue(option));
      this.closeOptions();
    },
    getOptionText(option) {
      if (option && this.labelProp) {
        let label = this.resolvePath(option, this.labelProp);
        if (label) return label;
      }
      return option;
    },
    getOptionId(option) {
      if (option && this.idProp) {
        let id = this.resolvePath(option, this.idProp);
        if (id) return id;
      }
      let value = this.getOptionValue(option);
      if (value) return value;
      return option;
    },
    getOptionValue(option) {
      if (option && this.valueProp) {
        let value = this.resolvePath(option, this.valueProp);
        if (value) return value;
      }
      return option;
    },
    resolvePath(obj, path) {
      if (!obj || !path) return null;
      return path.split('.').reduce((o, i) => o[i], obj);
    },
  },
};
</script>

<style lang="scss">
@import '~simplebar/dist/simplebar.css';

.form-select {
  display: inline-block;
  padding: 0;
  position: relative;
  width: 100%;

  &__input {
    background-color: $color-white;
    border: 1px solid $color-border;
    border-radius: $radius-md;
    color: $color-normal;
    cursor: pointer;
    font-size: 14px;
    min-height: 42px;
    outline: none;
    padding: 0 12px;
    transition: background 200ms ease, border 200ms ease, box-shadow 200ms ease;
    width: 100%;

    &.has-gray-background {
      background-color: $color-neutral-pale;
    }

    &:hover,
    &:active {
      border-color: $color-normal-pale;
    }

    &:focus {
      border-color: $color-accent;
      box-shadow: 0 0 0 3px rgba($color-accent-500, 0.1);
    }
  }

  &::after {
    border-top: 5px solid $color-normal-pale;
    border-right: 5px solid transparent;
    border-left: 5px solid transparent;
    content: '';
    display: block;
    position: absolute;
    right: 10px;
    top: calc(50% - 2px);
  }

  &.has-error {
    .form-select__input {
      border-color: $color-error;
    }
  }

  &__loading-bar {
    margin-top: 1px;
    position: absolute;
    left: 0;
    right: 0;
    width: 100%;
  }

  &__error-msg {
    bottom: -20px;
    color: $color-error;
    font-size: 12px;
    font-style: italic;
    position: absolute;
    right: 0;
  }

  &__menu {
    background-color: $color-white;
    border-radius: $radius-md;
    box-shadow: $shadow-dropdown;
    left: 0;
    margin: 6px 0 0;
    max-height: 400px;
    min-width: 340px;
    overflow-x: hidden;
    padding: 6px 0;
    position: absolute;
    top: 40px;
    width: 100%;
    z-index: $third-index;
  }

  &__list {
    font-size: 14px;
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: left;
  }

  &__item {
    list-style: none;
    margin: 0;
    padding: 0;
    width: 100%;
  }

  &__option {
    color: $color-normal;
    cursor: pointer;
    display: block;
    padding: 4px 16px;
    text-decoration: none;

    &:hover,
    &:active,
    &.active {
      color: $color-neutral;
    }
    &.selected,
    &.selected.active,
    &.selected:hover {
      color: $color-accent;
    }
  }
}
</style>
